import { Intent } from "@blueprintjs/core";
import axios from "axios";
import classNames from "classnames";
import { ButtonSave } from "components/buttons";
import { Breadcrumbs, VerticalLinkGroup } from "components/navigation";
import { AlertUnsavedData, NotificationToaster } from "components/notifications";
import { CircleStatus } from "components/status";
import { UserAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { TemplateDetails } from "pages/checks/templates/shared/template-details";
import { TemplateHistory } from "pages/checks/templates/edit/template-history";
import { TemplateRolePermissions } from "pages/checks/templates/shared/template-role-permissions";
import { TemplateSettings } from "pages/checks/templates/shared/template-settings";
import { PageRestricted } from "pages/errors/page-restricted";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AssetService, LocationService, UserService } from "services";
import { CheckService } from "services/CheckService";
import { useTemplateConfig } from "pages/checks/hooks/useTemplateConfig";
import { TemplateTypes } from "pages/checks/constants/TemplateTypes";

export function EditTemplate(props) {
    const { id } = useParams();
    const { useGlobalEndpoint, requiredAction, baseRoute, canEditGlobal } = useTemplateConfig(props.useGlobalConfig);

    // Modify the breadcrumb paths to include the builder link with the correct ID
    const breadcrumbs = useMemo(() => {
        if (props.breadcrumbs && props.breadcrumbs.length >= 2) {
            // Create a modified copy of the breadcrumbs with builder link
            return [
                props.breadcrumbs[0], // CHECK TEMPLATES
                { location: `${baseRoute}/${id}`, title: "TEMPLATE BUILDER" }, // Add dynamic TEMPLATE BUILDER link
                props.breadcrumbs[props.breadcrumbs.length - 1] // TEMPLATE SETTINGS
            ];
        }
        return props.breadcrumbs;
    }, [props.breadcrumbs, id]);

    const routes = [
        { location: `${baseRoute}/${id}/settings/details`, title: "Details" },
        { location: `${baseRoute}/${id}/settings/settings`, title: "Settings" },
        { location: `${baseRoute}/${id}/settings/role-permissions`, title: "Role Permissions" },
        { location: `${baseRoute}/${id}/settings/history`, title: "History" }
    ];

    // The numeric settings are seeded with the value their inputs report for an absent value, so the
    // page does not read as edited while the template is still loading.
    const defaultTemplate = {
        name: "",
        status: "Draft",
        subCategory: "",
        rolesWhoCanCompleteChecks: [],
        minimumTimeRequiredToFillOutCheckInMinutes: 0,
        timeThresholdInMinutes: 0,
        percentageOfChecksThatRequireRandomImage: 0
    }

    const [template, setTemplate] = useState(defaultTemplate);
    const [originalTemplate, setOriginalTemplate] = useState(defaultTemplate);
    const [category, setCategory] = useState("");

    const [templateOptions, setTemplateOptions] = useState({
        templateCategories: []
    })

    const [userRoles, setUserRoles] = useState([]);
    const [assetOptions, setAssetOptions] = useState({});
    const [locationBusinessAreas, setLocationBusinessAreas] = useState([]);

    const [loading, setLoading] = useState(true);
    const [isValid, setIsValid] = useState(true);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [saving, setSaving] = useState(false);

    const [statusColour, setStatusColour] = useState("")

    const isTemplateDirty = useDirtyData(originalTemplate, template);

    useEffect(() => {
        axios.all([
            CheckService.getTemplate(id, useGlobalEndpoint),
            CheckService.getTemplateOptions(),
            AssetService.getAssetOptions(useGlobalEndpoint),
            LocationService.getLocationBusinessAreas(),
            UserService.getRoles()
        ]).then(axios.spread((templateResponse, templateOptionsResponse, assetOptionsResponse, locationBusinessAreasResponse, userRolesResponse) => {
            setUserRoles(userRolesResponse);
            setLocationBusinessAreas(locationBusinessAreasResponse);

            let filteredAssetOptions = assetOptionsResponse;
            if (templateResponse.type === TemplateTypes.GLOBAL && filteredAssetOptions?.assetTypes) {
                filteredAssetOptions = {
                    ...filteredAssetOptions,
                    assetTypes: filteredAssetOptions.assetTypes.map(type => ({
                        ...type,
                        subtypes: []
                    }))
                };
            }

            setAssetOptions(filteredAssetOptions);
            // A version 1 template stores no image check percentage. The numeric input reports the
            // absent value back as 0, so seed both baselines with 0 or the tab reads as edited on load.
            const loadedTemplate = {
                ...templateResponse,
                percentageOfChecksThatRequireRandomImage: templateResponse.percentageOfChecksThatRequireRandomImage ?? 0
            };
            setTemplate(loadedTemplate);
            setOriginalTemplate({ ...loadedTemplate });
            setTemplateOptions(templateOptionsResponse);
            setLoading(false);
            setIsReadOnly(templateResponse.status !== "Draft" || 
              (!canEditGlobal && templateResponse.type === TemplateTypes.GLOBAL)
            )

        }),
        () => {
            NotificationToaster.show(Intent.DANGER, "Could not load template. Please try again.");
        })
    }, [id]);

    useEffect(() => {
        if (template.status === "Draft") {
            setStatusColour("FF7454")
        } else if (template.status === "Archived") {
            setStatusColour("EC2D6D")
        } else {
            setStatusColour("00C6B2")
        }
    }, [template]);

    useEffect(() => {
        if (template.subCategory && templateOptions) {
            setCategory(calculateCategoryFromSubCategory());
        }
    }, [template, templateOptions])

    const calculateCategoryFromSubCategory = () => {
        return templateOptions.templateCategories
            .find(c => c.subCategories.some(s => s.key === template.subCategory))
            ?.key;
    }

    const saveTemplate = () => {
        setSaving(true);

        if (!isValid) {
            NotificationToaster.show(Intent.WARNING, "There are errors on the page that need your attention");
            setSaving(false);
            return;
        }

        const templateToSave = {
            $subCategory: template.subCategory,
            ...template
        }

        if (noLongerAvailableAssetTypes?.length) {
            template.conditions.assetTypes = template.conditions.assetTypes.filter(a => !noLongerAvailableAssetTypes.includes(a));
        }

        if (noLongerAvailableSubTypes?.length) {
            template.conditions.assetSubTypes = template.conditions.assetSubTypes.filter(a => !noLongerAvailableSubTypes.includes(a));
        }

        CheckService.updateTemplate(templateToSave, useGlobalEndpoint).then(
            () => {
                setSaving(false);
                setOriginalTemplate({...template});
                NotificationToaster.show(Intent.SUCCESS, "Template updated successfully.");
            },
            (err) => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, err);
            }
        );
    }

    const assetSubTypes = assetOptions?.assetTypes?.flatMap(a => a.subtypes ?? []) ?? [];

    const getNoLongerAvailableTypes = (lookups, conditions) => {
        const lookupExists = (typeId) => lookups.some(t => t.id === +typeId);

        if (lookups && conditions) {
            return conditions
                .filter(a => !lookupExists(a));
        }

        return [];
    }

    const noLongerAvailableAssetTypes = useMemo(() => getNoLongerAvailableTypes(assetOptions?.assetTypes, template?.conditions?.assetTypes), 
        [assetOptions, template?.conditions?.assetTypes]);

    const showAssetTypeExclusionWarning = useMemo(() => !!noLongerAvailableAssetTypes.length, [noLongerAvailableAssetTypes]);

    const noLongerAvailableSubTypes = useMemo(() => getNoLongerAvailableTypes(assetSubTypes, template?.conditions?.assetSubTypes), 
        [assetOptions, template?.conditions?.assetSubTypes]);

    const showAssetSubTypeWarning = useMemo(() => !!noLongerAvailableSubTypes.length, [noLongerAvailableSubTypes]);

    const renderSaveButton = () => (
        <div className="row">
            <ButtonSave
                id="btn-template-edit-save"
                onClick={saveTemplate}
                disabled={saving}
                simpleDisabled={isReadOnly}
                loading={loading}
            ></ButtonSave>
        </div>)

    return (
        <UserAccess perform={[requiredAction]}
                yes={() => (
                    <Fragment>
                        <div className="row">

                            <Breadcrumbs items={breadcrumbs} />

                            <div className={classNames("inline-items", "space-medium", "spacer-bottom", { "bp6-skeleton": loading })}>
                                <CircleStatus background={statusColour} tooltip={template.status} />
                                <h1 className="no-margin">{originalTemplate.name}</h1>
                            </div>
                        </div>
                        <div className="layout-cols">
                            <div className="col side-col">
                                <div className="row">
                                    <VerticalLinkGroup links={routes}></VerticalLinkGroup>
                                </div>
                            </div>
                            <div className="col content-col">
                                <Routes>
                                    <Route path="details" element={<><TemplateDetails
                                            template={template}
                                            onChange={setTemplate}
                                            loading={loading}
                                            saving={saving}
                                            onValidationChange={setIsValid}
                                            isReadOnly={isReadOnly}
                                            isNew={false}
                                            templateOptions={templateOptions}
                                            assetOptions={assetOptions}
                                            locationBusinessAreas={locationBusinessAreas}
                                            showAssetTypeExclusionWarning={showAssetTypeExclusionWarning}
                                            showAssetSubTypeExclusionWarning={!showAssetTypeExclusionWarning && showAssetSubTypeWarning}
                                        />
                                        {renderSaveButton()}</>} />
                                    <Route path="settings" element={<><TemplateSettings
                                            template={template}
                                            onChange={setTemplate}
                                            loading={loading}
                                            saving={saving}
                                            onValidationChange={setIsValid}
                                            isReadOnly={isReadOnly}
                                            isNew={false}
                                            category={category}
                                            templateOptions={templateOptions}
                                        />
                                        {renderSaveButton()}</>} />
                                    <Route path="role-permissions" element={<><TemplateRolePermissions
                                            template={template}
                                            onChange={setTemplate}
                                            loading={loading}
                                            saving={saving}
                                            onValidationChange={setIsValid}
                                            isReadOnly={isReadOnly}
                                            isNew={false}
                                            userRoles={userRoles}
                                        />
                                        {renderSaveButton()}</>} />
                                    <Route path="history" element={<TemplateHistory
                                            templateId={id} useGlobalConfig={props.useGlobalConfig}
                                        />} />
                                    <Route path="" element={<Navigate to={`${baseRoute}/${id}/settings/details`} replace />} />
                                </Routes>
                            </div>
                        </div>
                        <AlertUnsavedData
                            isDirty={isTemplateDirty && !isReadOnly}>
                        </AlertUnsavedData>
                    </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />     
    );
}